import { useEffect, useState } from 'react';

import { Badge } from '@/components/common/Badge/Badge';
import { Button } from '@/components/common/Button/Button';
import { Card } from '@/components/common/Card/Card';
import { PageBreadcrumb } from '@/components/layout/PageBreadcrumb/PageBreadcrumb';
import { StepHeader } from '@/components/layout/StepHeader/StepHeader';
import {
  methodologySourceById,
  methodologySources,
} from '@/data/matchPoliticoMethodology';
import type { PoliticalMatchQuestion } from '@/models/political-match';
import { getPoliticalMatchQuestions, politicalMatchQuestionFallbacks } from '@/services/political-match-service';
import { useBrowserQueryCache } from '@/lib/query-cache/use-browser-query-cache';
import { useSeo } from '@/hooks/useSeo';
import { buildPageTitle } from '@/lib/seo';

import layoutStyles from '@/styles/layout.module.css';
import styles from '@/styles/match-politico.module.css';

function getAxisLabel(axis: 'economic' | 'personal'): string {
  return axis === 'economic' ? 'Dinheiro e trabalho' : 'Vida pessoal';
}

function getDirectionLabel(axis: 'economic' | 'personal', direction: 1 | -1): string {
  if (axis === 'economic') {
    return direction === 1 ? 'Concordar puxa para menos Estado' : 'Concordar puxa para mais Estado';
  }

  return direction === 1 ? 'Concordar puxa para mais liberdade' : 'Concordar puxa para menos liberdade';
}

function formatWeight(weight: number): string {
  return Number.isInteger(weight) ? String(weight) : weight.toFixed(1).replace('.', ',');
}

export function MatchPoliticoMethodologyPage(): JSX.Element {
  const queryCache = useBrowserQueryCache();
  const [questions, setQuestions] = useState<PoliticalMatchQuestion[]>(politicalMatchQuestionFallbacks);

  useSeo({
    title: buildPageTitle('Metodologia do Match Político'),
    description:
      'Entenda como o Match Político escolhe perguntas, calcula posições e identifica propostas em documentos oficiais.',
    canonicalPath: '/match-politico/metodologia',
    ogType: 'article',
  });

  useEffect(() => {
    let active = true;

    void getPoliticalMatchQuestions({ cache: queryCache }).then((loadedQuestions) => {
      if (!active || loadedQuestions.length === 0) {
        return;
      }

      setQuestions(loadedQuestions);
    });

    return () => {
      active = false;
    };
  }, [queryCache]);

  const quickQuestionCount = questions.filter((question) => question.isQuick).length;
  const completeQuestionCount = questions.length;

  return (
    <section className={styles.page}>
      <PageBreadcrumb items={[{ label: 'Match Político', to: '/match-politico' }, { label: 'Metodologia' }]} />

      <StepHeader
        kicker="Metodologia"
        title="Como funciona o Match Político?"
        description=""
        actions={
          <Button to="/match-politico" variant="secondary" size="sm">
            Voltar
          </Button>
        }
      />

      <section className={styles.section} aria-labelledby="eixos-title">
        <div>
          <p id="eixos-title" className={layoutStyles.sectionTitle}>
            Os dois eixos
          </p>
          <p className={layoutStyles.sectionSubtitle}>
            O mapa usa duas dimensões para simplificar a leitura de posições políticas.
          </p>
        </div>

        <div className={styles.axesGrid}>
          <Card className={styles.axesCard}>
            <div className={styles.axesChart} aria-hidden="true">
              <span className={styles.axesAxisVertical} />
              <span className={styles.axesAxisHorizontal} />
              <span className={styles.axesOrigin} />
              <span className={styles.axesTopLabel}>Mais liberdade pessoal</span>
              <span className={styles.axesRightLabel}>Mais liberdade econômica</span>
              <span className={styles.axesBottomLabel}>Menos liberdade pessoal</span>
              <span className={styles.axesLeftLabel}>Menos liberdade econômica</span>
            </div>
          </Card>

          <div className={styles.axesCopyGrid}>
            <Card className={styles.axisCopyCard}>
              <Badge tone="primary">Dinheiro e trabalho</Badge>
              <p className={styles.axisCopyText}>
                Considera tributação, regulação, atuação do Estado, iniciativa privada, relações trabalhistas, serviços públicos e políticas redistributivas.
                Uma posição mais alta no eixo indica maior preferência por autonomia econômica e menor intervenção estatal.
              </p>
            </Card>

            <Card className={styles.axisCopyCard}>
              <Badge tone="primary">Vida pessoal</Badge>
              <p className={styles.axisCopyText}>
                Considera liberdade de expressão, privacidade, autonomia individual, religião e Estado, segurança versus direitos individuais e decisões sobre a vida privada.
                Uma posição mais alta no eixo indica maior preferência por autonomia individual.
              </p>
            </Card>
          </div>
        </div>
      </section>

      <section className={styles.section} aria-labelledby="fontes-title">
        <div>
          <p id="fontes-title" className={layoutStyles.sectionTitle}>
            Como as perguntas são escolhidas
          </p>
          <p className={layoutStyles.sectionSubtitle}>
            As perguntas foram desenvolvidas pelo verdeconfirma com base em referências nacionais e internacionais de pesquisa política.
          </p>
        </div>

        <Card tone="soft">
          <div className={styles.sourceGrid}>
            {methodologySources.map((source) => (
              <article key={source.id} className={styles.sourceCard}>
                <p className={styles.sourceName}>{source.name}</p>
                <p className={styles.sourceDescription}>{source.description}</p>
              </article>
            ))}
          </div>
        </Card>
      </section>

      <section className={styles.section} aria-labelledby="perguntas-title">
        <div>
          <p id="perguntas-title" className={layoutStyles.sectionTitle}>
            Fontes por pergunta
          </p>
          <p className={layoutStyles.sectionSubtitle}>
            Abra cada item para ver a pergunta, o tema, o eixo, a direção do cálculo e as referências metodológicas usadas.
          </p>
        </div>

        <Card tone="soft" className={styles.questionsCard}>
          <div className={styles.questionsMetaRow}>
            <Badge tone="neutral">Match Rápido: {quickQuestionCount} perguntas</Badge>
            <Badge tone="neutral">Match Completo: {completeQuestionCount} perguntas</Badge>
          </div>

          <div className={styles.questionList}>
            {questions.map((question) => (
              <details key={question.id} className={styles.questionItem}>
                <summary className={styles.questionSummary}>
                  <span className={styles.questionSummaryMain}>
                    <span className={styles.questionSummaryBadges}>
                      <Badge tone="primary">{getAxisLabel(question.axis)}</Badge>
                      {question.isQuick ? <Badge tone="neutral">Match Rápido</Badge> : null}
                    </span>
                    <span className={styles.questionText}>{question.statement}</span>
                  </span>

                  <span className={styles.questionSummaryFooter}>
                    <span className={styles.questionSummaryMeta}>
                      {question.dimension} • {question.methodologySourceIds.length} referências
                    </span>
                    <span className={styles.questionSummaryHint}>Abrir</span>
                  </span>
                </summary>

                <div className={styles.questionContent}>
                  <dl className={styles.questionMetaGrid}>
                    <div>
                      <dt className={styles.questionMetaLabel}>Tema</dt>
                      <dd className={styles.questionMetaValue}>{question.dimension}</dd>
                    </div>
                    <div>
                      <dt className={styles.questionMetaLabel}>Eixo</dt>
                      <dd className={styles.questionMetaValue}>{getAxisLabel(question.axis)}</dd>
                    </div>
                    <div>
                      <dt className={styles.questionMetaLabel}>Direção</dt>
                      <dd className={styles.questionMetaValue}>{getDirectionLabel(question.axis, question.direction)}</dd>
                    </div>
                    <div>
                      <dt className={styles.questionMetaLabel}>Peso</dt>
                      <dd className={styles.questionMetaValue}>{formatWeight(question.weight)}</dd>
                    </div>
                  </dl>
  
                  <div>
                    <p className={styles.questionReferencesTitle}>Referências metodológicas</p>
                    <div className={styles.questionReferences}>
                      {question.methodologySourceIds.map((sourceId) => {
                        const source = methodologySourceById[sourceId];

                        return (
                          <article key={source.id} className={styles.questionReferenceItem}>
                            <p className={styles.questionReferenceName}>{source.name}</p>
                            <p className={styles.questionReferenceDescription}>{source.description}</p>
                          </article>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </details>
            ))}
          </div>
        </Card>
      </section>

      <section className={styles.section} aria-labelledby="calculo-title">
        <div>
          <p id="calculo-title" className={layoutStyles.sectionTitle}>
            Como calculamos o resultado
          </p>
          <p className={layoutStyles.sectionSubtitle}>
            A fórmula é a mesma para usuários e candidatos depois que as posições são convertidas para a mesma escala.
          </p>
        </div>

        <div className={styles.calculationGrid}>
          <Card className={styles.calculationCard}>
            <p className={styles.cardTitle}>Resposta do usuário</p>
            <p className={styles.cardText}>
              As respostas internas vão de <strong>-2</strong> a <strong>+2</strong>. Respostas de <strong>Não sei</strong> ou <strong>prefiro não responder</strong> ficam fora do cálculo.
            </p>

            <pre className={styles.formulaBlock} aria-label="Fórmula do cálculo">
Σ(resposta × direção × peso)
────────────────────────────
máximo possível respondido
            </pre>

            <p className={styles.cardText}>
              Perguntas não respondidas saem do denominador para que ausência de opinião não vire uma posição de centro.
            </p>
          </Card>

          <Card className={styles.calculationCard}>
            <p className={styles.cardTitle}>Match Rápido e Match Completo</p>
            <div className={styles.stack}>
              <div>
                <p className={styles.stackLabel}>Match Rápido</p>
                <p className={styles.cardText}>8 perguntas. Usa um subconjunto da versão completa.</p>
              </div>
              <div>
                <p className={styles.stackLabel}>Match Completo</p>
                <p className={styles.cardText}>24 perguntas. Reaproveita as respostas já dadas no modo rápido.</p>
              </div>
            </div>
          </Card>

          <Card className={styles.calculationCard}>
            <p className={styles.cardTitle}>Como identificamos as posições dos candidatos?</p>
            <p className={styles.cardText}>
              Os candidatos não respondem ao questionário do verdeconfirma. As posições são identificadas a partir das propostas presentes nos documentos oficiais analisados.
            </p>

            <ol className={styles.processList}>
              <li>Pergunta</li>
              <li>Busca nas propostas</li>
              <li>Trecho relevante</li>
              <li>Interpretação</li>
              <li>Classificação</li>
            </ol>

            <p className={styles.cardText}>
              Cada classificação precisa de evidência documental. Quando não há base suficiente, o candidato aparece como <strong>Sem posição suficiente</strong>.
            </p>
          </Card>
        </div>
      </section>

      <section className={styles.section} aria-labelledby="evidencia-title">
        <div>
          <p id="evidencia-title" className={layoutStyles.sectionTitle}>
            Evidência obrigatória e mesma régua
          </p>
          <p className={layoutStyles.sectionSubtitle}>
            Quando a posição do candidato é convertida para a escala de -2 a +2, o cálculo usa a mesma fórmula aplicada ao usuário.
          </p>
        </div>

        <div className={styles.evidenceGrid}>
          <Card className={styles.evidenceCard}>
            <p className={styles.cardTitle}>Escala de classificação</p>
            <ul className={styles.scaleList}>
              <li><strong>+2</strong> Concorda claramente</li>
              <li><strong>+1</strong> Tende a concordar</li>
              <li><strong>0</strong> Posição intermediária</li>
              <li><strong>-1</strong> Tende a discordar</li>
              <li><strong>-2</strong> Discorda claramente</li>
              <li><strong>null</strong> Sem posição suficiente</li>
            </ul>
          </Card>

          <Card className={styles.evidenceCard}>
            <p className={styles.cardTitle}>Como isso aparece na prática</p>
            <div className={styles.evidenceExample}>
              <p className={styles.evidenceExampleLabel}>Tende a concordar</p>
              <p className={styles.evidenceExampleMeta}>Fonte</p>
              <p className={styles.evidenceExampleValue}>Plano de Governo 2026</p>
              <p className={styles.evidenceExampleMeta}>Página 42</p>
              <p className={styles.evidenceExampleQuote}>"Trecho utilizado..."</p>
              <p className={styles.evidenceExampleMeta}>Como interpretamos</p>
              <p className={styles.evidenceExampleText}>A proposta prevê...</p>
            </div>
          </Card>
        </div>
      </section>

      <section className={styles.section} aria-labelledby="limites-title">
        <div>
          <p id="limites-title" className={layoutStyles.sectionTitle}>
            O que não fazemos, privacidade e limitações
          </p>
          <p className={layoutStyles.sectionSubtitle}>
            A ferramenta é informativa, simples e auditável, mas não substitui leitura crítica ou análise completa das candidaturas.
          </p>
        </div>

        <div className={styles.limitGrid}>
          <Card className={styles.limitCard}>
            <p className={styles.cardTitle}>O Match Político não</p>
            <ul className={styles.bulletList}>
              <li>recomenda candidato;</li>
              <li>determina em quem votar;</li>
              <li>calcula percentual de compatibilidade;</li>
              <li>produz ranking de candidatos;</li>
              <li>considera ausência de proposta como neutralidade;</li>
              <li>usa respostas pessoais do usuário para classificar candidatos.</li>
            </ul>
          </Card>

          <Card className={styles.limitCard}>
            <p className={styles.cardTitle}>Privacidade</p>
            <p className={styles.cardText}>
              Suas respostas permanecem armazenadas somente no seu navegador. O backend não recebe respostas, pontuação econômica, pontuação pessoal ou posição no gráfico.
            </p>
            <p className={styles.cardText}>
              O e-mail usado para liberar a visualização dos Matchs Políticos é armazenado separadamente e não é associado às respostas políticas locais.
            </p>
          </Card>

          <Card className={styles.limitCard}>
            <p className={styles.cardTitle}>Limitações da ferramenta</p>
            <ul className={styles.bulletList}>
              <li>posições políticas são mais complexas do que dois eixos;</li>
              <li>um plano de governo não cobre todos os temas;</li>
              <li>a escala de cinco posições simplifica nuances reais;</li>
              <li>a ferramenta representa apenas os temas presentes no questionário;</li>
              <li>classificações podem ser revisadas com novas evidências oficiais.</li>
            </ul>
          </Card>
        </div>
      </section>

      <Card tone="soft" className={styles.footerCard}>
        <p className={styles.footerText}>
          O Match Político é uma ferramenta de caráter informativo e de entretenimento criada para facilitar a exploração de posições políticas e propostas eleitorais.
        </p>
        <p className={styles.footerText}>
          O resultado não representa recomendação de voto, apoio político ou avaliação da qualidade de qualquer candidatura.
        </p>
      </Card>
    </section>
  );
}
